/**
 * Big Two bot.
 *
 * - Leading: opens with the cheapest multi-card combination it can build (pairs, triples, five-card hands) so
 *   several cards go in one play, otherwise its lowest single. The first play must hold the three of diamonds, so it
 *   picks the largest low combination containing it.
 * - Following: plays the cheapest set that beats the pile, but passes rather than spend a two or an ace on a small
 *   pile while it still holds many cards; it always plays if that empties its hand.
 * The bot only ever reads its own seat's view.
 */
import { type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { bigTwoCombo, bigTwoLegal, bigTwoPower, bigTwoRankPower, type BigTwoAction, type BigTwoLegal, type BigTwoView } from './engine.js';

const value = (set: readonly CardId[]) => bigTwoCombo(set)?.value ?? 0;
const topRank = (set: readonly CardId[]) => Math.max(...set.map(bigTwoRankPower));

function choose(view: BigTwoView, legal: BigTwoLegal, ctx: BotContext): BigTwoAction {
  const sets = legal.sets;
  const finisher = sets.find((s) => s.length === view.hand.length);
  if (finisher) return { type: 'play', cards: finisher };

  if (view.need === 0) {
    // Lead low, and shed as many cards as possible in one play: prefer the biggest set whose top rank is modest.
    const low = sets.filter((s) => topRank(s) <= 11);
    const pool = low.length > 0 ? low : sets;
    const biggest = Math.max(...pool.map((s) => s.length));
    const best = pool.filter((s) => s.length === biggest).sort((a, b) => value(a) - value(b))[0]!;
    const single = [...sets.filter((s) => s.length === 1)].sort((a, b) => value(a) - value(b))[0];
    // A lone single beats a poor five-card dump (a five-card play of mostly high cards): keep it simple and take
    // the multi-card play only when it is not a high-rank one.
    if (view.firstPlay) return { type: 'play', cards: best };
    return { type: 'play', cards: biggest > 1 && topRank(best) <= 11 ? best : (single ?? best) };
  }
  if (sets.length === 0) return { type: 'pass' };
  const cheapest = [...sets].sort((a, b) => value(a) - value(b))[0]!;
  const careful = ctx.profile.memory > 0 && view.hand.length > 4;
  if (careful && topRank(cheapest) >= 14) return { type: 'pass' };
  return { type: 'play', cards: cheapest };
}

export const bigTwoBot: BotEngine<BigTwoView, BigTwoLegal, BigTwoAction> = {
  decideAction(view, legal, ctx) {
    if (legal.sets.length === 0) return { type: 'pass' };
    if (chance(ctx, ctx.profile.mistakeRate)) {
      if (legal.canPass && chance(ctx, 0.5)) return { type: 'pass' };
      return { type: 'play', cards: legal.sets[ctx.rng.int(legal.sets.length)]! };
    }
    return choose(view, legal, ctx);
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 26));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, view.handCounts[view.seat]! / 13);
  },

  handleTimeout(view, legal) {
    const now = legal.sets.length > 0 || legal.canPass ? legal : bigTwoLegal(view.hand, view.pile, view.firstPlay);
    if (now.canPass) return { type: 'pass' };
    return { type: 'play', cards: [...now.sets].sort((a, b) => a.length - b.length || bigTwoPower(a[0]!) - bigTwoPower(b[0]!))[0]! };
  },
};
